import React from 'react';
import { formatPct } from '../../../common/cardStats/formatters';
import { AffiliateLink } from '../../../../affiliates/components/AffiliateLink';
import { humanizeUpgradesEnum } from './labelUtils';

/**
 * Renderers for the Upgrade Finder result grid.
 *
 * The new /upgrades API returns several row shapes depending on the
 * `source` param:
 *   - card-row: the common shape (staples, synergy_*, manabase,
 *     archetype, category_role, legacy category). Rendered as
 *     `<UpgradelizerCardTile>`.
 *   - combo row: members + missing-count annotations. Rendered as
 *     `<UpgradelizerComboRow>`.
 *   - synergy row (top_synergies): anchor card + category + rate.
 *     Rendered as `<UpgradelizerSynergyRow>`.
 *
 * Each renderer normalizes its item to the shape expected by
 * `useOpenCardInfo` so click-through to the card info dialog always
 * works.
 */

/**
 * Thin tile for the "role" / "staples" / "synergy_*" / "manabase" /
 * "archetype" / "category_role" sources. All of those return a
 * baseline card row; the component optionally surfaces inclusion-rate
 * and source-specific badges (supporter count, anchor metadata).
 */
export function UpgradelizerCardTile({ item, onSelectCard, onShowAllMatches, showUsage, badge, priceInfo }) {
    const usagePct = showUsage && item?.inclusionRate != null ? formatPct(item.inclusionRate) : null;

    // matches[] is populated by the card_category source (the unified
    // /upgrades base-search overhaul). Each entry describes one scoring
    // entry that satisfied the active filters — effectType + a short
    // conditionText snippet. We surface only the top match inline and
    // expose "See all" when matchCount exceeds matches.length.
    const matches = Array.isArray(item?.matches) ? item.matches : [];
    const topMatch = matches[0];
    const matchCount = typeof item?.matchCount === 'number' ? item.matchCount : matches.length;
    const matchesTruncated = item?.matchesTruncated === true || matchCount > matches.length;
    const showSeeAll = matchesTruncated && typeof onShowAllMatches === 'function' && matchCount > 1;
    // category is "all categories this card scored in" on the new
    // contract — pick the first when launching the match-detail dialog
    // (it's per-category) so the user sees the strongest scoring lane.
    const categoryForDetail = topMatch?.category || item?.categories?.[0] || null;

    // TCGPlayer affiliate target for the price chip. Prefer Scryfall's
    // direct product URL; fall back to a search by card name when the
    // particular print has no tcgplayer purchase_uri. Clicks must not
    // also bubble to the tile's onSelectCard, hence stopPropagation.
    const tcgTarget = (() => {
        if (!priceInfo) return null;
        if (priceInfo.purchaseUris?.tcgplayer) {
            return { kind: 'card', card: { purchaseUris: priceInfo.purchaseUris } };
        }
        if (item?.name) return { kind: 'search', query: item.name };
        return null;
    })();
    const priceLabel = priceInfo?.usd != null && Number.isFinite(priceInfo.usd) ? `$${priceInfo.usd.toFixed(2)}` : null;

    return (
        <div className="upgradelizerCardTile">
            <button
                type="button"
                className="upgradelizerCardTileBody"
                onClick={() =>
                    onSelectCard({
                        ...item,
                        id: item?.cardId || item?.id,
                    })
                }
                title={`View ${item?.name || 'card'}`}
            >
                <div className="upgradelizerCardHeader">
                    <span className="upgradelizerCardName">{item?.name}</span>
                    {usagePct && (
                        <span className="upgradelizerCardUsage" title={`Run by ${usagePct} of decks at this bin`}>
                            {usagePct}
                        </span>
                    )}
                </div>
                <div className="upgradelizerCardImgWrap">
                    {item?.imageUri ? (
                        <img src={item.imageUri} alt={item.name || ''} className="upgradelizerCardImg" loading="lazy" />
                    ) : (
                        <div className="upgradelizerCardImgPlaceholder" aria-hidden="true" />
                    )}
                    {badge && <span className="upgradelizerCardBadge">{badge}</span>}
                </div>
            </button>
            {topMatch && (
                <div
                    className="upgradelizerCardMatch"
                    title={
                        topMatch?.conditionText
                            ? `${humanizeUpgradesEnum(topMatch.effectType || 'match')}: "${topMatch.conditionText}"`
                            : humanizeUpgradesEnum(topMatch?.effectType)
                    }
                >
                    <span className="upgradelizerCardMatchType">
                        {humanizeUpgradesEnum(topMatch.effectType || 'match')}
                    </span>
                    {topMatch.conditionText && (
                        <span className="upgradelizerCardMatchText">"{topMatch.conditionText}"</span>
                    )}
                    {showSeeAll && (
                        <button
                            type="button"
                            className="upgradelizerCardMatchMore"
                            onClick={(e) => {
                                e.stopPropagation();
                                onShowAllMatches({
                                    cardId: item?.cardId || item?.id,
                                    cardName: item?.name,
                                    category: categoryForDetail,
                                });
                            }}
                            title={`See all ${matchCount} matches`}
                        >
                            +{matchCount - matches.length} more
                        </button>
                    )}
                </div>
            )}
            {priceInfo === undefined ? (
                <div
                    className="upgradelizerCardPrice upgradelizerCardPrice--skeleton"
                    aria-busy="true"
                    aria-label="Loading price"
                >
                    <span className="upgradelizerCardPriceSkelBar" />
                </div>
            ) : tcgTarget ? (
                <AffiliateLink
                    provider="tcgplayer"
                    target={tcgTarget}
                    placement="upgradelizer"
                    className={`upgradelizerCardPrice${priceLabel ? '' : ' upgradelizerCardPrice--noPrice'}`}
                    title={
                        priceLabel
                            ? `Buy ${item?.name} on TCGPlayer (cheapest: ${priceLabel}) — affiliate link, supports the site`
                            : `Buy ${item?.name} on TCGPlayer — affiliate link, supports the site`
                    }
                    aria-label={
                        priceLabel
                            ? `Buy ${item?.name} on TCGPlayer for ${priceLabel}`
                            : `Buy ${item?.name} on TCGPlayer`
                    }
                    onClick={(e) => e.stopPropagation()}
                >
                    {priceLabel || 'Buy on TCG'}
                </AffiliateLink>
            ) : null}
        </div>
    );
}

/**
 * Combo-row card for `source=combos`. Renders each member card as a
 * mini-thumbnail with an in-deck/missing state, plus combo-level metadata
 * (inclusion rate, wincon tag, missing-count badge).
 */
export function UpgradelizerComboRow({ item, onSelectCard, priceByName }) {
    const cards = item?.cards || [];
    const missingCount = item?.missingCount ?? 0;
    const totalCount = cards.length;
    const inclusionPct = item?.inclusionRate != null ? formatPct(item.inclusionRate) : null;
    const effectTags = item?.categories || [];
    const isWincon = item?.type === 'wincon';

    const handleComboClick = () => {
        if (item?.spellbookUri) {
            try {
                window.open(item.spellbookUri, '_blank', 'noopener');
            } catch (_err) {
                // popup blocked — ignore
            }
        }
    };

    const missingBadge =
        missingCount === 0 ? 'Complete' : missingCount === 1 ? '1 card away' : `${missingCount} cards away`;

    return (
        <div className={`upgradelizerComboRow${isWincon ? ' upgradelizerComboRow--wincon' : ''}`}>
            <div className="upgradelizerComboHeader">
                <div className="upgradelizerComboHeaderLeft">
                    {isWincon && (
                        <span className="upgradelizerComboWinconTag" title="Combo wins the game outright">
                            Wincon
                        </span>
                    )}
                    <span
                        className={`upgradelizerComboMissing${
                            missingCount === 1 ? ' upgradelizerComboMissing--closeout' : ''
                        }`}
                    >
                        {missingBadge}
                    </span>
                    {effectTags.length > 0 && (
                        <span className="upgradelizerComboEffects" title={effectTags.join(', ')}>
                            {effectTags.slice(0, 3).join(' · ')}
                            {effectTags.length > 3 ? ` +${effectTags.length - 3}` : ''}
                        </span>
                    )}
                </div>
                <div className="upgradelizerComboHeaderRight">
                    {inclusionPct && (
                        <span
                            className="upgradelizerComboRate"
                            title={`${item?.count || 0} decks at this bin run this combo`}
                        >
                            {inclusionPct}
                        </span>
                    )}
                    {item?.spellbookUri && (
                        <button
                            type="button"
                            className="upgradelizerComboSpellbookLink"
                            onClick={handleComboClick}
                            aria-label="View combo on Commander Spellbook"
                            title="View combo on Commander Spellbook"
                        >
                            ↗
                        </button>
                    )}
                </div>
            </div>
            <div className={`upgradelizerComboCards${totalCount <= 2 ? ' upgradelizerComboCards--pair' : ''}`}>
                {cards.map((card, idx) => {
                    const priceInfo = priceByName?.[card?.name?.toLowerCase()];
                    const priceLabel =
                        priceInfo?.usd != null && Number.isFinite(priceInfo.usd)
                            ? `$${priceInfo.usd.toFixed(2)}`
                            : null;
                    const tcgTarget = (() => {
                        if (!priceInfo) return null;
                        if (priceInfo.purchaseUris?.tcgplayer) {
                            return { kind: 'card', card: { purchaseUris: priceInfo.purchaseUris } };
                        }
                        if (card?.name) return { kind: 'search', query: card.name };
                        return null;
                    })();
                    // Only missing combo pieces get a buy chip — owned
                    // cards are already in the user's deck, no need.
                    const showBuyChip = !card?.inDeck;
                    return (
                        <div
                            key={`${card?.cardId || idx}`}
                            className={`upgradelizerComboCardWrap${
                                card?.inDeck
                                    ? ' upgradelizerComboCardWrap--owned'
                                    : ' upgradelizerComboCardWrap--missing'
                            }`}
                        >
                            <button
                                type="button"
                                className={`upgradelizerComboCard${
                                    card?.inDeck ? ' upgradelizerComboCard--owned' : ' upgradelizerComboCard--missing'
                                }`}
                                onClick={() => onSelectCard({ ...card, id: card?.cardId })}
                                title={`${card?.name || 'card'}${
                                    card?.inDeck ? ' — already in your deck' : ' — missing'
                                }`}
                            >
                                {card?.imageUri ? (
                                    <img
                                        src={card.imageUri}
                                        alt={card.name || ''}
                                        className="upgradelizerComboCardImg"
                                        loading="lazy"
                                    />
                                ) : (
                                    <div
                                        className="upgradelizerCardImgPlaceholder upgradelizerComboCardImg"
                                        aria-hidden="true"
                                    />
                                )}
                                <span className="upgradelizerComboCardBadge">{card?.inDeck ? '✓' : '+'}</span>
                                <span className="upgradelizerComboCardName">{card?.name}</span>
                            </button>
                            {showBuyChip &&
                                (priceInfo === undefined ? (
                                    <div
                                        className="upgradelizerComboCardPrice upgradelizerComboCardPrice--skeleton"
                                        aria-busy="true"
                                        aria-label="Loading price"
                                    >
                                        <span className="upgradelizerCardPriceSkelBar" />
                                    </div>
                                ) : tcgTarget ? (
                                    <AffiliateLink
                                        provider="tcgplayer"
                                        target={tcgTarget}
                                        placement="upgradelizer-combo"
                                        className={`upgradelizerComboCardPrice${
                                            priceLabel ? '' : ' upgradelizerComboCardPrice--noPrice'
                                        }`}
                                        title={
                                            priceLabel
                                                ? `Buy ${card?.name} on TCGPlayer (cheapest: ${priceLabel}) — affiliate link, supports the site`
                                                : `Buy ${card?.name} on TCGPlayer — affiliate link, supports the site`
                                        }
                                        aria-label={
                                            priceLabel
                                                ? `Buy ${card?.name} on TCGPlayer for ${priceLabel}`
                                                : `Buy ${card?.name} on TCGPlayer`
                                        }
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {priceLabel || 'Buy on TCG'}
                                    </AffiliateLink>
                                ) : null)}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * Synergy-pattern row for `source=top_synergies`. Each item is a
 * synergy entry keyed to an anchor card + category + rate stats.
 */
export function UpgradelizerSynergyRow({ item, onSelectCard }) {
    const anchor = item?.anchorCard || {};
    const deckRate = item?.deckRate != null ? formatPct(item.deckRate) : null;
    const avgScore = item?.avgScore != null && Number.isFinite(item.avgScore) ? item.avgScore.toFixed(1) : null;

    return (
        <div className="upgradelizerSynergyRow">
            <button
                type="button"
                className="upgradelizerSynergyAnchor"
                onClick={() => onSelectCard({ ...anchor, id: anchor?.cardId })}
                title={`View ${anchor?.name || 'anchor card'}`}
            >
                {anchor?.imageUri ? (
                    <img
                        src={anchor.imageUri}
                        alt={anchor.name || ''}
                        className="upgradelizerSynergyAnchorImg"
                        loading="lazy"
                    />
                ) : (
                    <div className="upgradelizerCardImgPlaceholder upgradelizerSynergyAnchorImg" aria-hidden="true" />
                )}
                <span className="upgradelizerSynergyAnchorName">{anchor?.name}</span>
            </button>
            <div className="upgradelizerSynergyBody">
                <div className="upgradelizerSynergyBodyRow">
                    {item?.category && <span className="upgradelizerSynergyCategory">{item.category}</span>}
                    {deckRate && (
                        <span className="upgradelizerSynergyRate" title={`${item?.deckCount || 0} decks`}>
                            {deckRate} of decks
                        </span>
                    )}
                    {avgScore && (
                        <span className="upgradelizerSynergyScore" title="Average supporter + enabler score">
                            ★ {avgScore}
                        </span>
                    )}
                </div>
                {item?.text && <div className="upgradelizerSynergyText">{item.text}</div>}
                <div className="upgradelizerSynergyStats">
                    {item?.avgSupporters != null && (
                        <span className="upgradelizerSynergyStatPill">{item.avgSupporters.toFixed(1)} supporters</span>
                    )}
                    {item?.avgEnablers != null && (
                        <span className="upgradelizerSynergyStatPill">{item.avgEnablers.toFixed(1)} enablers</span>
                    )}
                </div>
            </div>
        </div>
    );
}
